import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RefreshCw, Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { emails as api } from '@/api/endpoints'
import { Alert, Input, Select, Spinner } from '@/components/ui'
import EmailList from '@/features/emails/EmailList'
import { STATUS_ORDER } from '@/features/emails/status'
import { cn } from '@/lib/utils'
import type { EmailStatus } from '@/types/api'

/** Druhy správ, ktoré systém posiela. Filtruje sa nimi, nevymýšľajú sa. */
const KINDS = ['notification', 'password-reset', 'receipt', 'test']

/**
 * Prehľad odchádzajúcej pošty.
 *
 * Odpovedá na tri otázky, ktoré si škola kladie: čo sme komu poslali, či to
 * dorazilo, a čo sa zaseklo. Prepínač stavov nesie počty, takže „päť
 * zlyhaných“ je vidieť skôr, než niekto začne hľadať.
 */
export default function EmailsPage() {
  const { t } = useTranslation()
  const [status, setStatus] = useState<EmailStatus | ''>('')
  const [kind, setKind] = useState('')
  const [search, setSearch] = useState('')
  const qc = useQueryClient()

  const { data: summary, isLoading } = useQuery({
    queryKey: ['email-summary'],
    queryFn: api.summary,
    // Worker medzitým odosiela, takže počty starnú rýchlo.
    refetchInterval: 30_000,
  })

  const generate = useMutation({
    mutationFn: api.generate,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['emails'] })
      void qc.invalidateQueries({ queryKey: ['email-summary'] })
    },
  })

  const total = Object.values(summary ?? {}).reduce((sum, n) => sum + (n ?? 0), 0)
  const stuck = (summary?.failed ?? 0) + (summary?.bounced ?? 0)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-semibold">{t('emails.title')}</h1>
        {isLoading ? <Spinner /> : null}
        <button
          type="button"
          onClick={() => generate.mutate()}
          disabled={generate.isPending}
          title={t('emails.generateHint')}
          className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          {generate.isPending ? <Spinner className="h-3 w-3" /> : <RefreshCw size={12} />}
          {t('emails.generate')}
        </button>
        {generate.isSuccess ? (
          <span className="text-xs text-slate-500">
            {generate.data.enabled
              ? t('emails.generated', {
                  created: generate.data.created,
                  queued: generate.data.queued,
                })
              : t('emails.notificationsOff')}
          </span>
        ) : null}
        {generate.isError ? (
          <span className="text-xs text-red-600">
            {apiErrorMessage(generate.error, t('errors.generic'))}
          </span>
        ) : null}
        <div className="relative ml-auto">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('emails.searchPlaceholder')}
            className="w-72 pl-8"
          />
        </div>
        <Select value={kind} onChange={(e) => setKind(e.target.value)} className="w-44">
          <option value="">{t('emails.allKinds')}</option>
          {KINDS.map((value) => (
            <option key={value} value={value}>
              {t(`emails.kind.${value}`)}
            </option>
          ))}
        </Select>
      </div>

      {stuck > 0 ? (
        <Alert variant="warning">{t('emails.stuckWarning', { count: stuck })}</Alert>
      ) : null}

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        <Tab
          label={t('emails.all')}
          count={total}
          active={status === ''}
          onClick={() => setStatus('')}
        />
        {STATUS_ORDER.map((value) => (
          <Tab
            key={value}
            label={t(`emails.status.${value}`)}
            count={summary?.[value] ?? 0}
            active={status === value}
            onClick={() => setStatus(value)}
          />
        ))}
      </div>

      <div className="card overflow-x-auto">
        <EmailList
          // Zmena filtra musí zoznam postaviť odznova - inak by po prepnutí
          // stavu zostala otvorená tretia strana, ktorá v novom výbere
          // neexistuje.
          key={`${status}|${kind}|${search}`}
          filter={{
            status: status || undefined,
            kind: kind || undefined,
            search: search || undefined,
          }}
        />
      </div>
    </div>
  )
}

function Tab({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'border-b-2 px-3 py-1.5 text-sm',
        active
          ? 'border-brand-500 font-medium text-brand-700'
          : 'border-transparent text-slate-500 hover:text-slate-800',
      )}
    >
      {label}
      {count > 0 ? <span className="ml-1.5 text-xs text-slate-400">{count}</span> : null}
    </button>
  )
}
